import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import { line, area, curveMonotoneX } from 'd3-shape';
import { select } from 'd3-selection';
import first from 'lodash.first';
import last from 'lodash.last';

import generateRandomHash from 'helpers/generateRandomHash';
import { round } from 'components/data-visualization/LineChart/helpers/math';
import computeNotNullSegments from './utils/computeNotNullSegments';
import getMeasureUnits from 'components/data-visualization/LineChart/helpers/getMeasureUnit';

import Gradient from 'components/data-visualization/DoubleChart/Gradient';
import MissingValues from './MissingValues';
import { HorizontalAxe, VerticalAxe } from './Axes';

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

const LeftTextLabel = styled('text')<any>`
  text-anchor: middle;
  font-family: 'Proxima Nova';
  font-size: 10px;
  text-transform: uppercase;
  fill: ${({ theme }): string => theme.CasperGrey};
`;

const Path = styled('path')`
  stroke-dasharray: 4 4;
  stroke: ${({ color }): any => color};
`;

export type marginType = {
  left: number;
  right: number;
  betweenGraphs: number;
  topHorizontalAxe: number;
};

/**
 * This component, given the correct props, returns a LineChart with one or multiple trend
 * lines, configured axes' and an (optional) interactive tracker.
 *
 * @param {object} props React props
 * @param {string} props.color Color
 * @param {array} props.entities Array of entities to visualise
 * @param {string} props.entityId Entity id
 * @param {number} props.heightBottomLabel height of the bottom ticks label
 * @param {string} props.label Text label on the left
 * @param {number} props.offset Vertical offset for the second graph
 * @param {number} props.parentHeight The height of the parent container
 * @param {number} props.parentWidth The width of the parent container
 * @param {string} props.yKey The name of the field in the data to get y value
 */

interface IDoubleLineChart {
  color: string;
  entities: any[];
  entityId: string;
  heightBottomLabel: number;
  label: string;
  margin: marginType;
  offset?: number;
  parentHeight: number;
  parentWidth: number;
  widthLeftLabel: number;
  yKey: string;
  xScale: any;
  yScale: any;
  hasBeginDottedChart: boolean;
  showHorizontalAxe: boolean;
}

const DoubleLineChart: React.FC<IDoubleLineChart> = (props) => {
  const {
    color,
    entities = [],
    entityId,
    heightBottomLabel,
    label,
    margin,
    offset = 0,
    parentWidth,
    parentHeight,
    widthLeftLabel,
    yKey,
    xScale,
    yScale = noop,
    hasBeginDottedChart,
    showHorizontalAxe,
  } = props;

  const [isLastNull, setIsLastNull] = useState<any>();
  const [isFirstNull, setIsFirstNull] = useState<any>();
  const [lastNullSegment, setLastNullSegment] = useState<any>();
  const [firstNullSegment, setFirstNullSegment] = useState<any>();
  useEffect(() => {
    if (
      entities &&
      entities.length > 0 &&
      entities.some((item: any) => !!item[yKey])
    ) {
      const nullSegments = entities.reduce(
        (segments: any, current: any, index: number, arr: any) => {
          if (index === 0 && current[yKey] === null) {
            segments.push([index]);
            return segments;
          }
          if (current[yKey] === null) {
            if (arr[index - 1][yKey] === null) {
              const lastSegment = segments[segments.length - 1];
              lastSegment.push(index);
            } else {
              segments.push([index]);
            }
          }
          return segments;
        },
        []
      );
      setLastNullSegment(nullSegments.slice(-1));
      setFirstNullSegment(nullSegments.slice(0, 1));
      setIsLastNull(entities[entities.length - 1][yKey] === null);
      setIsFirstNull(entities[0][yKey] === null);
    }
  }, [entities, yKey]);

  const randomHash = useRef(generateRandomHash());

  const width = useMemo(() => {
    if (parentWidth === 0) return parentWidth;
    return round(parentWidth - margin.left - margin.right - widthLeftLabel);
  }, [margin, parentWidth, widthLeftLabel]);

  const height = useMemo(() => {
    return round(parentHeight - heightBottomLabel - margin.topHorizontalAxe);
  }, [margin, parentHeight, heightBottomLabel]);

  const isNullYValues = entities.some((item: any) => item[yKey] === null);
  const notNullSegments =
    isNullYValues && entities ? computeNotNullSegments(entities, yKey) : [];
  const notNullPlaylistData =
    entities && entities.filter((item: any) => item[yKey] !== null);

  const pathGenerator = useCallback(
    line()
      .x((d: any) => xScale(d.periodKey))
      .y((d: any) => yScale(d[yKey]))
      .curve(curveMonotoneX),
    [xScale, yScale]
  );

  const graphElement = useRef(null);
  select(graphElement.current)
    .datum(notNullPlaylistData)
    .attr('fill', 'none')
    .attr('stroke', color)
    .attr('stroke-width', 2)
    .attr('d', pathGenerator);
  const gradient = useCallback(
    area()
      .defined(() => true)
      .x((d: any) => xScale(d.periodKey))
      .y0(() => height + heightBottomLabel)
      .y1((d: any) => yScale(d[yKey]))
      .curve(curveMonotoneX),
    [xScale, yScale]
  );

  const graphDashedElement = useRef(null);
  select(graphDashedElement.current)
    .datum(notNullPlaylistData)
    .attr('fill', 'none')
    .attr('stroke', color)
    .attr('stroke-width', 2)
    .attr('d', pathGenerator);

  const isPosition = label === 'Position';
  const measurements =
    !isPosition &&
    yScale &&
    yScale.hasOwnProperty('ticks') &&
    yScale.ticks().filter((tick: number) => !!tick).length > 0 &&
    getMeasureUnits(yScale.ticks(), 'number');
  const labelText = isPosition
    ? label
    : measurements
    ? `${label} in ${measurements}`
    : label;

  const xScaleValues = xScale
    .ticks()
    .reduce(
      (prev: any, item: any, i: number) =>
        i === 0 || !(i % 4) ? [...prev, item] : prev,
      []
    );
  const getPathPlotValues = useCallback(
    (segment: any): any => {
      const start: any = first(segment);
      const end: any = last(segment);
      const segmentWidth = xScale(end.periodKey) - xScale(start.periodKey);
      return {
        start,
        end,
        segmentWidth: segmentWidth > 0 ? segmentWidth : 0,
      };
    },
    [xScale]
  );

  return (
    <g
      transform={`translate(0, ${
        label === 'Streams' ? margin.betweenGraphs : 0
      })`}
    >
      <LeftTextLabel
        transform={`rotate(-90)`}
        y="20"
        dx={-(parentHeight / 2) - offset / 2}
      >
        {labelText}
      </LeftTextLabel>

      <g
        transform={`translate(${
          widthLeftLabel + margin.left
        }, ${heightBottomLabel})`}
      >
        <HorizontalAxe
          height={height}
          label={label}
          margin={margin}
          offset={offset}
          width={width}
          xScale={xScale}
          xScaleValues={xScaleValues}
          yScale={yScale}
          showHorizontalAxe={showHorizontalAxe}
        />
        <VerticalAxe label={label} width={width} yScale={yScale} />
        {/* Graph line */}
        <path
          ref={graphElement}
          clipPath={
            isNullYValues
              ? `url(#line-clip-path-${entityId}-${randomHash.current})`
              : undefined
          }
        />
        {/* Covering null values in the middle of graph */}
        <defs>
          {isNullYValues && (
            <clipPath id={`line-clip-path-${entityId}-${randomHash.current}`}>
              {(notNullSegments || []).map((segment: any) => {
                const { start, end, segmentWidth } = getPathPlotValues(segment);

                /* Add y=-2px to display properly the whole chart line on top and exclude overlapping */
                return (
                  <rect
                    fill={'black'}
                    key={`segment-${start.periodKey}-${end.periodKey}`}
                    x={xScale(start.periodKey)}
                    y="-2px"
                    height={offset + parentHeight}
                    width={segmentWidth}
                  />
                );
              })}
            </clipPath>
          )}

          <mask id={`gaps-mask-${entityId}-${randomHash.current}`}>
            <rect
              x={margin.left + widthLeftLabel}
              y="0"
              width={width}
              height={offset + parentHeight}
              fill="white"
            />
            {(notNullSegments || []).map((segment: any) => {
              const { start, end, segmentWidth } = getPathPlotValues(segment);

              return (
                <rect
                  fill={'black'}
                  key={`inverse-segment-${start.periodKey}-${end.periodKey}`}
                  x={margin.left + widthLeftLabel + xScale(start.periodKey)}
                  y="0"
                  height={offset + parentHeight}
                  width={segmentWidth}
                />
              );
            })}
          </mask>
        </defs>

        {isNullYValues && <Path ref={graphDashedElement} color={color} />}

        {/* Gradient */}
        <Gradient
          color={color}
          data={notNullPlaylistData}
          gradient={gradient}
          keyValue="playlist-positions"
        />

        {/* Missing data from the beginning */}
        {hasBeginDottedChart && isFirstNull && yScale && (
          <MissingValues
            color={color}
            entities={entities}
            isFirst={true}
            nullSegment={firstNullSegment}
            xScale={xScale}
            yKey={yKey}
            yScale={yScale}
          />
        )}

        {/* Missing data in the end */}
        {isLastNull && yScale && (
          <MissingValues
            color={color}
            entities={entities}
            isFirst={false}
            nullSegment={lastNullSegment}
            xScale={xScale}
            yKey={yKey}
            yScale={yScale}
          />
        )}
      </g>
    </g>
  );
};

export default DoubleLineChart;
